import { Button, Modal, Text, View } from '@zinc/ui';
import type React from 'react';
import { createContext, useCallback, useContext, useState } from 'react';

export enum AreYouSureText {
  Default = 'Are you sure you want to do this?',
  Delete = 'Are you sure you want to delete this?',
  Unsaved = 'You have unsaved changes. Would you like to save them before closing?'
}

type AreYouSureOptions = {
  title?: string;
  text?: string | AreYouSureText;
  yesText?: string;
  noText?: string;
  hideNoButton?: boolean;
  verboseNoClick?: boolean;
  onYesColor?: 'primary' | 'critical' | 'positive' | 'neutral' | 'media' | 'inherit' | undefined;
  onYes?: () => void;
  onNo?: () => void;
};

type AreYouSureContextType = {
  onOpen: (options?: AreYouSureOptions) => void;
};

const AreYouSureContext = createContext<AreYouSureContextType | undefined>(undefined);

export const useAreYouSure = () => {
  const ctx = useContext(AreYouSureContext);
  if (!ctx) throw new Error('useAreYouSure must be used within AreYouSureProvider');
  return ctx;
};

export const AreYouSureProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<AreYouSureOptions>({});
  const { hideNoButton } = options;
  const onOpen = useCallback((opts?: AreYouSureOptions) => {
    setOptions(opts || {});
    setOpen(true);
  }, []);

  const handleYes = useCallback(() => {
    setOpen(false);
    options.onYes?.();
  }, [options]);

  const handleNo = useCallback(() => {
    if (options.hideNoButton) return;
    setOpen(false);
    options.onNo?.();
  }, [options]);

  const onClose = useCallback(() => {
    if (options.verboseNoClick) return;
    setOpen(false);
    options.onNo?.();
  }, [options]);

  return (
    <AreYouSureContext.Provider value={{ onOpen }}>
      {children}
      <Modal active={open} onClose={onClose} attributes={{ id: 'are-you-sure-modal' }}>
        <View padding={2} justify="center" gap={4}>
          <Modal.Title>{options.title || 'Are You Sure?'}</Modal.Title>
          <Text>{options.text || AreYouSureText.Default}</Text>
          <View direction="row" justify="end" align="center" gap={2}>
            {!hideNoButton && (
              <Button variant="ghost" onClick={handleNo}>
                {options.noText || 'No'}
              </Button>
            )}
            <Button color={options.onYesColor || 'primary'} onClick={handleYes}>
              {options.yesText || 'Yes'}
            </Button>
          </View>
        </View>
      </Modal>
    </AreYouSureContext.Provider>
  );
};
